import Accordion from 'react-bootstrap/Accordion'
import Breadcrumb from '../components/Breadcrumb'
import Card from 'react-bootstrap/Card'
import Container from 'react-bootstrap/Container'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router-dom'
import { OutboundLink } from 'react-ga'
import { initialize } from '../store/pagedata'
import { useDispatch } from 'react-redux'
import { useEffect } from 'react'

const Faq = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(initialize())
  }, [])

  const Header = (props) => (
    <Card.Header {...props}>
      <span style={{ fontWeight: 500 }}>{props.children}</span>
    </Card.Header>
  )

  const data = [
    {
      body: (
        <>
          EDHREC is a deck analysis tool for the Commander format (a.k.a. EDH)
          of Magic: the Gathering. EDHREC computes decklist statistics from
          popular deck-building websites, helping players new and old build
          decks of their own.
          <br />
          <br />
          Not sure where to get started? Read{' '}
          <OutboundLink
            eventLabel='https://draftsim.com/how-to-use-edhrec/'
            rel='noopener noreferrer'
            target='_blank'
            to='https://draftsim.com/how-to-use-edhrec/'
          >
            How to Use EDHREC to Build an Awesome Commander Deck
          </OutboundLink>{' '}
          on Draftsim.com.
        </>
      ),
      header: 'Help! What is EDHREC?',
    },
    {
      body: (
        <>
          Many cards across EDHREC will have a rating of synergy that will show
          up as a percentage that either has a + or a - in front of it. For
          example, this Eldrazi Displacer has a +75% synergy rating on the
          Rasputin Dreamweaver page. <br /> <br />
          Synergy rating is intended to show cards that are particularly
          important to the commander or theme you are currently viewing. This is
          opposed to just cards that are generally played in most decks that are
          popular because they are individually strong. We did this because we
          wanted to show what cards were interesting to a commander in the
          Signature Cards section at the top of the page. It gives a quick look
          at what cards define a deck, as opposed to just showing a bunch of
          cards like Cyclonic Rift and Sol Ring that aren’t interesting because
          they are in a bunch of decks. <br /> <br />
          Synergy score is calculated as: (% decks this card is in for
          commander/theme) - (% decks this card is in for color identity) <br />{' '}
          <br />
          In the Eldrazi Displacer and Rasputin Dreamweaver example, a +75%
          synergy score means that it’s seen way more than in other U/W decks.
          It’s calculated by taking the 86% of Rasputin decks it is in, then
          subtracting the 11% of other U/W decks it is in. Leaving us +75%
          synergy score, making it pretty clear this card is not generally
          popular in most U/W decks but particularly popular in Rasputin decks.
        </>
      ),
      header: 'What is "Synergy"? What is the "Signature Cards" section?',
    },
    {
      body: (
        <>
          EDHREC collects deck data from Archidekt, MTGGoldfish, Aetherhub,
          CommanderSpellbook, Moxfield, Scryfall and Deckstats. We collect data
          daily from these pages and new data is reflected most often on pages
          within a few days. Tappedout was a source of data in the early stages
          of the website so some decks may still appear, but we are not
          collecting data from them currently. <br /> <br />
          If your deck is on one of those deck building sites, chances are it’ll
          get pulled in. However, there are a number of things that can prevent
          a deck from being pulled in. Most notably, make sure it’s a legal
          deck. If you have too many cards, too few cards, cards out of your
          color identity, or banned cards, our database will not pick it up
          intentionally. <br /> <br />
          However, there are a number of things that can go wrong between EDHREC
          and the source websites and there is no way to notify you that
          something went wrong. Sometimes they may not be your fault, or
          anyone’s fault. We simply can’t guarantee that every deck makes it up
          on EDHREC. <br /> <br />
          If you update your deck on the deck building site, EDHREC will collect
          it as a new deck and delete our old one, effectively updating the deck
          in our database. Updating your deck a bunch of times does not make
          duplicates of the deck in our database.
        </>
      ),
      header:
        'Where does EDHREC collect its deck data from? How can I get my deck on EDHREC? What happens when I update my deck?',
    },
    {
      body: (
        <>
          On the{' '}
          <Link target='_blank' to='/themes'>
            themes page
          </Link>
          , and the{' '}
          <Link target='_blank' to='/tribes'>
            tribes page
          </Link>
          , there are dozens of different themes and tribes listed. How these
          are actually defined are not scientific and are simply the developers
          of EDHREC fiddling with some counts until it appears that the page
          provides decent results. Each theme also is defined individually and
          we don’t have one defined formula that covers all themes. <br />{' '}
          <br />
          As such, we honestly don’t have that good of an answer to this
          question. We hope the end resulting pages provide some value to people
          trying to build those themes, but there are plenty of decks that
          simply didn’t meet the definition but probably could be considered
          that from a human perspective.
        </>
      ),
      header: 'How are themes defined? How are tribes defined?',
    },
    {
      body: (
        <>
          Salt Scores are based on an annual survey of commander players. Users
          were showed a card and asked on a scale of 0-4 how salty that card
          made them. We collected the results and now have{' '}
          <Link target='_blank' to='/top/salt'>
            a top list of the saltiest cards
          </Link>{' '}
          and also the score appears on cards with a high enough salt score.{' '}
          <br /> <br />
          Read{' '}
          <a
            href='https://edhrec.com/articles/new-edhrec-feature-salt/'
            rel='noreferrer'
            target='_blank'
          >
            this article
          </a>{' '}
          for more information.
        </>
      ),
      header: 'What is the Salt Score?',
    },
    {
      body: (
        <>
          We can only handle youtube content right now. So if you are a podcast,
          or whatever you are, you need to have a youtube channel.{' '}
          <Link target='_blank' to='/contact-us'>
            Contact us
          </Link>{' '}
          with a link to your channel and a quick description of what you are
          trying to do. <br /> <br />
          We have some basic requirements for channels on the community content
          feed:
          <br />
          <br />
          <ul>
            <li>
              Must have generally good quality (audio / video). While it doesn’t
              need to be professional quality, some effort should be apparent in
              the content.
            </li>
            <li>
              At least 10 videos posted in a consistent manner. We want to be
              sure you are in it for the long haul and many content producers
              drop off before 10 episodes.
            </li>
            <li>
              A single youtube playlist sorted by most recent video first, so
              our automated systems can tell us when you upload new content.
              Youtube allows videos to be added to more than one playlist, so
              making an additional "All Episodes" type of playlist can be useful
              if you already have episodes organized into several smaller lists
              and like it that way.
            </li>
            <li>
              We allow content geared towards adults and are typically fine with
              profanity and crude humor. However, we will not show content
              producers that are encouraging or participating in harassment,
              discrimination, or any sort of negative behavior towards
              individual people or groups of people.
            </li>
          </ul>
        </>
      ),
      header:
        'How do I get my content up on the “Community Content” feed on the front page?',
    },
    {
      body: (
        <>
          Generally, we’re always willing to bring on new writers.{' '}
          <Link target='_blank' to='/apply'>
            Apply to write for EDHREC
          </Link>{' '}
          with a description of a series you’d like to write for us. Writing
          samples are a big plus on moving things along quickly.
        </>
      ),
      header: 'How do I become an author for EDHREC?',
    },
    {
      body: (
        <>
          We can run custom ad campaigns for companies and individuals that want
          to target EDHREC users.{' '}
          <Link target='_blank' to='/contact-us'>
            Contact us
          </Link>{' '}
          and we can send over the ad units we have available and some more
          information on what we can do. <br /> <br />
          In general, we will only advertise products that we as EDHREC can
          stand behind in terms of quality and value. If your product is well
          known we might be to able to determine the reputation publicly, but if
          not we may request some samples (we can pay for them if the campaign
          is large enough) to review them for ourselves. For payment, we are
          fine with cost per click, cost per impression, and/or affiliate %
          structures. We’re pretty flexible so just email us!
        </>
      ),
      header:
        'I have something Commander players might like. Can I advertise on EDHREC?',
    },
  ]

  return (
    <>
      <Helmet>
        <meta name='description' content='Frequently Asked Questions' />
        <meta name='keywords' content='EDHREC,questions,faq,synergy,data' />
        <title>FAQ | EDHREC</title>
      </Helmet>
      <Breadcrumb breadcrumb={[{ '/faq': 'FAQ' }]} />
      <h4 className='page-heading'>Frequently Asked Questions</h4>
      <Container>
        <Accordion>
          {data.map(({ body, header }, i) => (
            <Card>
              <Accordion.Toggle as={Header} variant='link' eventKey={i + 1}>
                {header}
              </Accordion.Toggle>
              <Accordion.Collapse eventKey={i + 1}>
                <Card.Body>{body}</Card.Body>
              </Accordion.Collapse>
            </Card>
          ))}
        </Accordion>
      </Container>
    </>
  )
}

export default Faq
